/**
 * The browser half of the waitlist call.
 *
 * Both sides import the request and response types from src/shared/waitlist.ts,
 * so if the endpoint's shape changes, this file stops compiling. That is the
 * entire reason that file is shared rather than duplicated.
 */
import type { Locale, WaitlistRequest, WaitlistResponse } from '../../shared/waitlist';

export interface SubmitOptions {
  email: string;
  locale: Locale;
  /** Which CTA opened the modal — 'hero', 'nav', 'final-cta'. */
  source: string;
  /** The honeypot field's value. Always '' for a real person. */
  company: string;
}

/**
 * POST the signup.
 *
 * Never throws: a network failure comes back as a normal `ok: false` result, so
 * the component has exactly one shape to render and no try/catch of its own.
 */
export async function submitWaitlist(options: SubmitOptions): Promise<WaitlistResponse> {
  const body: WaitlistRequest = {
    email: options.email,
    locale: options.locale,
    source: options.source,
    company: options.company,
  };

  try {
    const response = await fetch('/api/waitlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    // The API returns JSON for every status it controls, but a proxy or an
    // outage can return HTML — so a parse failure is treated as a server error
    // rather than crashing the component.
    const data = (await response.json()) as WaitlistResponse;
    return data;
  } catch {
    return {
      ok: false,
      error: 'server_error',
      message: 'Could not reach the server.',
    };
  }
}
